import React from 'react';
import { cn } from '@/lib/utils';
import cherryBlossomBranch from '@/assets/bohostyleco-cherry-blossom-branch.png';

interface CherryBlossomDividerProps {
  className?: string;
  variant?: 'full' | 'center' | 'left' | 'right';
  size?: 'sm' | 'md' | 'lg';
  opacity?: number;
  withBackground?: boolean;
}

const CherryBlossomDivider: React.FC<CherryBlossomDividerProps> = ({
  className,
  variant = 'full',
  size = 'md',
  opacity = 0.8,
  withBackground = true
}) => {
  // Size configurations
  const sizeConfig = {
    sm: {
      height: 'h-16 md:h-20',
      maxWidth: 'max-w-md',
      scale: 'scale-75'
    },
    md: {
      height: 'h-20 md:h-24',
      maxWidth: 'max-w-2xl',
      scale: 'scale-90 md:scale-100'
    },
    lg: {
      height: 'h-24 md:h-32',
      maxWidth: 'max-w-4xl',
      scale: 'scale-100'
    }
  };

  // Variant configurations
  const variantConfig = {
    full: {
      containerClass: 'w-full flex justify-center',
      imageClass: 'w-full object-contain object-center'
    },
    center: {
      containerClass: 'w-full flex justify-center',
      imageClass: 'w-3/4 md:w-1/2 object-contain object-center'
    },
    left: {
      containerClass: 'w-full flex justify-start',
      imageClass: 'w-3/4 md:w-2/3 object-contain object-left'
    },
    right: {
      containerClass: 'w-full flex justify-end',
      imageClass: 'w-3/4 md:w-2/3 object-contain object-right'
    }
  };

  const config = sizeConfig[size];
  const variantStyles = variantConfig[variant];

  return (
    <div 
      className={cn(
        'relative w-full flex items-center justify-center py-8 md:py-12',
        className
      )}
      role="separator"
      aria-label="Cherry blossom decorative divider"
    >
      {/* Optional subtle background gradient */}
      {withBackground && (
        <div 
          className="absolute inset-0 bg-gradient-to-b from-transparent via-muted/10 to-transparent"
          aria-hidden="true"
        />
      )}
      
      {/* Cherry blossom branch container */}
      <div 
        className={cn(
          'relative z-10',
          config.height,
          config.maxWidth,
          variantStyles.containerClass
        )}
      >
        <img
          src={cherryBlossomBranch}
          alt="Cherry blossom branch decoration"
          className={cn(
            'h-full transition-all duration-700 ease-out',
            config.scale,
            variantStyles.imageClass
          )}
          style={{ 
            opacity,
            filter: 'brightness(1.3) contrast(1.2) saturate(1.1) drop-shadow(0 2px 12px hsl(var(--primary) / 0.25)) drop-shadow(0 0 20px hsl(var(--primary) / 0.15))'
          }}
          loading="lazy"
        />
      </div>

      {/* Enhanced glow effect for better visibility */}
      <div 
        className="absolute inset-0 bg-gradient-radial from-primary/8 via-primary/3 to-transparent opacity-60"
        aria-hidden="true"
      />
    </div>
  );
};

export default CherryBlossomDivider;